/*
*   File : kyc-verification.js
*   Author : https://evoqins.com
*   Description : Kyc verification page
*   Integrations : NA
*   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { format, subYears } from 'date-fns';
import { useRazorpay } from "react-razorpay";
import { Modal } from 'bootstrap';


//Style
import style from "../../Styles/Component/kyc-verification.module.scss"
import CardStyle from "../../Styles/Component/card.module.scss";
import Colors from "../../Styles/color.scss";

//Custom components
import { PrimaryButton, TextButton } from '../../Component/Buttons';
import { CustomDatePicker, CustomSelectBox, CustomSlider, CustomTextInput } from '../../Component/FormElements';
import { Chip, CustomRadioButton, Loader } from '../../Component/Others';
import { APIService } from '../../Service';
import { SubscriptionStatusModal } from '../../Component/Modal';
import { RiskProfiling } from '../Risk';
import { _getProfile, _scrollToTop } from '../../Helper';

// Assets
import CompletedStep from "../../Assets/Images/Basket/completed-step.svg"
import ActiveStep1 from "../../Assets/Images/Basket/active-one.svg"
import ActiveStep2 from "../../Assets/Images/Basket/active-two.svg"
import ActiveStep3 from "../../Assets/Images/Basket/active-three.svg"
import ActiveStep4 from "../../Assets/Images/Basket/active-four.svg"
import InActiveStep2 from "../../Assets/Images/Basket/inactive-two.svg"
import InActiveStep3 from "../../Assets/Images/Basket/inactive-three.svg"
import InActiveStep4 from "../../Assets/Images/Basket/inactive-four.svg"
import LoaderImg from "../../Assets/Images/app-loader.gif";

// Custom data
const MANDATE_TYPE = [
    {
        id: 1,
        label: "Net Banking"
    },
    {
        id: 2,
        label: "UPI"
    },
];

const CreateSubscription = () => {

    const { error: RazorPayError, isLoading, Razorpay } = useRazorpay();
    const USER_DATA = useSelector((store) => store.Reducer.USER_DATA);
    const { state } = useLocation();
    const scrollRef = useRef(null);
    const navigate = useNavigate();

    const [productData, setProductData] = useState(state && state.strategy_data ? JSON.parse(state.strategy_data) : "");
    let subscription_status = productData.subscription_status;
    let agreement_status = productData.agreement_status;
    let plan_info = productData && productData.pricing_information.find(plan => plan.id === state.plan_id);
    let is_agreement_required = productData && productData.creator_info.entity_type === "RIA" ? true : false;
    let current_step = (subscription_status === "EXPIRED" || subscription_status === "CANCELLED") ? 1 : is_agreement_required === false && subscription_status === "CREATED" ? 2 : agreement_status === "SIGNED" ? 4 : agreement_status === "REQUESTED" ? 3 : 1;
    let is_fixed_type = plan_info.fee_type === "FIXED";
    let tenant_id = productData && productData.creator_info.creator_id ? Number(productData.creator_info.creator_id) : null;



    const [step, setStep] = useState(current_step);
    const [loading, setLoading] = useState(false);
    const [btnLabel, setBtnLabel] = useState("");

    // Subscription data
    const [IGST, setIGST] = useState(0);
    const [SGST, setSGST] = useState(0);
    const [CGST, setCGST] = useState(0);
    const [amountPayable, setAmountPayable] = useState(0);
    const [showSubscriptionStatusModal, setShowSubscriptionStatusModal] = useState(false);
    const [showStatusLoader, setShowStatusLoader] = useState(false);
    const [selectedAmount, setSelectedAmount] = useState(Number(productData.planned_investment_amt) || productData.min_investment);
    const [advisoryFees, setAdvisoryFees] = useState(selectedAmount);
    const [mandateType, setMandateType] = useState(MANDATE_TYPE[0].id);
    const [mandateLoading, setMandateLoading] = useState(false);

    // KYC STEP - State
    const [pan, setPan] = useState("");
    const [panError, setPanError] = useState("");
    const [selectedDate, setSelectedDate] = useState(null);
    const [dateError, setDateError] = useState("");
    const [dateToggle, setDateToggle] = useState(false);
    const [states, setStates] = useState([]);
    const [stateDataLoading, setStateDataLoading] = useState(true);
    const [selectedState, setSelectedState] = useState("");
    const [stateError, setStateError] = useState("");

    const [dataLoading, setDataLoading] = useState(true);
    const [modalType, setModalType] = useState(1);

    useEffect(() => {
        if (!state) {
            navigate("/explore");
            return;
        };
        if (step === 1) {
            // API call
            _getStates();
        } else {
            setDataLoading(false);
        };


        // setting user selected state for GST calculation in resume flow
        if (productData.users_state) {
            setSelectedState({
                label: productData.users_state
            });
        }

    }, []);

    useEffect(() => {
        // Scroll the page to top
        _scrollToTop();
    }, [step]);

    // Updating Button label for subscription flow
    useEffect(() => {
        if (step === 2 || step === 4) {
            setBtnLabel("Setup AutoPay");
        } else {
            const btn_name = subscription_status === "CREATED" ? "Continue E-Sign" : "Start E-Sign";
            setBtnLabel(btn_name);
        }
    }, [step, productData]);

    // Subscription  flow info calculation - Initial UI
    useEffect(() => {

        if (state === null) {
            navigate("/explore");
            return;
        };

        let igst = 0, sgst = 0, cgst = 0, total_amount_payable = 0;

        if (is_fixed_type) {
            let plan_fee = Number(plan_info.fixed_fee);
            if (selectedState.label !== productData.creator_info.state) {
                igst = plan_fee * (Number(plan_info.igst) / 100);
                total_amount_payable = (plan_fee + igst);
            } else {
                sgst = plan_fee * (Number(plan_info.sgst) / 100);
                cgst = plan_fee * (Number(plan_info.cgst) / 100);
                total_amount_payable = (plan_fee + sgst + cgst);
            }
        } else {
            // Calculating subscription fees
            let subscription_fee = productData.min_investment * (Number(plan_info.percentage_fee) / 100);

            if (selectedState.label !== productData.creator_info.state) {
                igst = subscription_fee * (Number(plan_info.igst) / 100);
                total_amount_payable = subscription_fee + igst;
            } else {
                sgst = subscription_fee * (Number(plan_info.sgst) / 100);
                cgst = subscription_fee * (Number(plan_info.cgst) / 100);
                total_amount_payable = subscription_fee + sgst + cgst;
            }

            setAdvisoryFees(Number(subscription_fee.toFixed(2)).toLocaleString("en-IN")); // advisory fees

        }
        setIGST(Number(igst.toFixed(2)).toLocaleString("en-IN"));
        setSGST(Number(sgst.toFixed(2)).toLocaleString("en-IN"));
        setCGST(Number(cgst.toFixed(2)).toLocaleString("en-IN"));
        setAmountPayable(Number(total_amount_payable.toFixed(2)).toLocaleString("en-IN"));
    }, [selectedState]);

    useEffect(() => {
        if (showSubscriptionStatusModal === true) {
            const subscription_modal = new Modal(document.getElementById('subscription-modal'));
            subscription_modal.show();
        }
    }, [showSubscriptionStatusModal]);

    useEffect(() => {
        if (dateToggle === true) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    }, [dateToggle]);


    // handle back 
    const _handleBack = () => {

        if (step === 1 || (is_agreement_required && agreement_status === "REQUESTED" && step > 2) || (subscription_status === "CREATED" && step > 1)) {
            navigate("/explore/strategy-detail", {
                state: {
                    strategy_id: Number(productData.strategy_id)
                }
            });
        } else {
            setStep(step - 1);
        }
    };

    // handle navigate to creator detail page
    const _handleNavigateToCreatorDetail = (creator_id) => {
        navigate("/explore/creator-detail", {
            state: {
                creator_id: Number(creator_id)
            }
        });
    };

    // handle navigate to detail page 
    const _handleNavigateToDetailPage = () => {

        if (modalType === 2 || modalType === 4 || modalType === 0) {
            navigate("/explore/strategy-detail", {
                state: {
                    strategy_id: Number(productData.strategy_id)
                },
                replace: true
            });
        }

        // ModalType
        // KYC Verified === Type 1
        // KYC Not Verified === Type 2
        // Sign Successful === Type 3
        // Subscription Successful === Type 3

        if (modalType === 1) {
            // KYC verification => RiskProfile || Mandate Creation
            setStep(2);
        } else if (modalType === 3) {
            // Mandate - AutoPay step
            setStep(4);
        }

        setShowSubscriptionStatusModal(false);
    };

    // handle close status modal
    const _handleCloseStatusModal = () => {
        setShowSubscriptionStatusModal(false);
        if (modalType === 3) {
            setStep(4);
        } else if (modalType === 4) {
            navigate("/explore/strategy-detail", {
                state: {
                    strategy_id: Number(productData.strategy_id)
                },
                replace: true
            });
        }
    };

    // KYC FLOW FUNCTIONS //
    // handle verify pan an dob 
    const _handleVerifyKYC = () => {

        let is_valid = true;
        let validate_pan = /^[A-Z]{3}P[A-Z]{1}\d{4}[A-Z]{1}$/; // validation of PAN

        if (pan.trim().length === 0) {
            setPanError("Enter your PAN");
            is_valid = false;
        } else if (!validate_pan.test(pan)) {
            setPanError("Enter a valid PAN");
            is_valid = false;
        }

        if (selectedDate === null) {
            setDateError("Select your date of birth");
            is_valid = false;
        }

        if (selectedState === "") {
            setStateError("Select your state");
            is_valid = false;
        }

        if (is_valid === true) {

            setLoading(true);
            // API call
            _checkKYCStatus();
        }
    };

    // handle select state
    const _handleSelectState = (selected) => {
        setSelectedState(selected);
        setStateError("");
    };

    // handle clear date 
    const _handleClearDate = () => {
        setSelectedDate(null);
    };

    //handle pan change
    const _handlePanChange = (value) => {
        if (value.length <= 10) {
            setPan(value.toUpperCase());
            setPanError("");
        };
    };

    // handle select date
    const _handleSelectDate = (day) => {
        setSelectedDate(day);
        setDateToggle(false);
        setDateError("");
    };

    //handle date toggle show an hide
    const _handleDateToggle = () => {
        setDateToggle((prev) => !prev);

        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };


    // SUBSCRIPTION FLOW FUNCTIONS //
    // handle select product type
    const _handleSelectMandateType = (id) => {
        setMandateType(id);
    };

    // handle setup auto pay  
    const _handleSubscribe = () => {

        // When user come back to Create subscription step from AutoPay Step 
        if (agreement_status === "SIGNED" && subscription_status === "CREATED" && step == 3) {
            // Navigate to AutoPay step
            setStep(4);
            return;
        };

        setMandateLoading(true);
        let is_subscription_required = (subscription_status === null && agreement_status === "PENDING") || subscription_status === "EXPIRED" || subscription_status === "CANCELLED" ? true : false;

        if (is_subscription_required) {
            // Initiate subscription
            _createSubscription();
        } else if (subscription_status === "CREATED") {
            if (agreement_status === "SIGNED" || (agreement_status === "PENDING" && is_agreement_required === false)) {
                _authorizeSubscription(productData.subscription_id);
            } else {
                if (productData.agreement_generator_id) {
                    // Continue Agreement Sign Process
                    let identifier = USER_DATA.mobile;
                    let options = {
                        environment: "sandbox",
                        logo: require('../../Assets/Images/Navigation/indvestment-logo.svg').default,
                        theme: {
                            primaryColor: Colors.ocean_blue,
                            secondaryColor: Colors.black
                        },
                        callback: function (digio_response) {
                            if (digio_response.hasOwnProperty("error_code")) {
                                toast.dismiss();
                                toast.error(digio_response.message);
                                setMandateLoading(false);
                            } else {
                                _syncSubscription(productData.agreement_id);
                            }
                        },
                    };

                    let digio = new window.Digio(options);
                    digio.init();
                    digio.submit(productData.agreement_generator_id, identifier);

                } else {
                    _signSubscription(productData.subscription_id);
                }
            }
        }
    };

    // handle amount value change
    const _handleAmountValueChange = (value) => {


        let igst = 0, sgst = 0, cgst = 0, total_amount_payable = 0;
        setSelectedAmount(value);

        // Calculating subscription fees
        let subscription_fee = Number(value) * (Number(plan_info.percentage_fee) / 100);

        if (selectedState.label !== productData.creator_info.state) {
            igst = subscription_fee * (Number(plan_info.igst) / 100);
            total_amount_payable = subscription_fee + igst;
            setIGST(Number(igst.toFixed(2)).toLocaleString("en-IN"));
        } else {
            sgst = subscription_fee * (Number(plan_info.sgst) / 100);
            cgst = subscription_fee * (Number(plan_info.cgst) / 100);
            total_amount_payable = subscription_fee + sgst + cgst;
            setSGST(Number(sgst.toFixed(2)).toLocaleString("en-IN"));
            setCGST(Number(cgst.toFixed(2)).toLocaleString("en-IN"));
        };

        setAdvisoryFees(Number(subscription_fee.toFixed(2)).toLocaleString("en-IN")); // advisory fees
        setAmountPayable(Number(total_amount_payable.toFixed(2)).toLocaleString("en-IN")); // total amount payable
    };

    // handle risk profile success
    const _handleRiskProfileSuccess = () => {
        setStep(3);
    };


    // =============================================== //
    // ================= API calls =================== //
    // Get States list
    const _getStates = async () => {
        let is_post = false;
        let url = "kyc/state-list";

        const response = await APIService(url, null, is_post);
        if (response.status_code === 200) {

            let data = response.data.map(item => {
                return {
                    value: item.state_code,
                    label: item.state_name,
                }
            }).sort((a, b) => (a.label < b.label ? -1 : 1));

            setStates(data);
        } else {
            toast.dismiss();
            toast.error("Something went wrong, please try again after some time");
        }

        setStateDataLoading(false);
        setDataLoading(false);
    };

    // Check KYC status
    const _checkKYCStatus = async () => {
        let url = "kyc/check";
        let data = {
            "tenant_id": tenant_id,
            "user_id": USER_DATA.unique_identifier,
            "strategy_id": productData.strategy_id,
            "pan_no": pan,
            "state_code": String(selectedState.value),
            "dob": format(selectedDate, 'dd-MM-yyyy'),
            "source": "WEB"
        };

        const response = await APIService(url, data);
        if (response.status_code === 200) {
            setShowSubscriptionStatusModal(true);
            setModalType(response.data.kyc_status === "KYC Not Compliant" ? 2 : 1);
        } else {

            // Temp fix
            if (USER_DATA?.kyc_status === true) {
                setShowSubscriptionStatusModal(true);
                setModalType(1);
            } else {
                toast.dismiss();
                toast.error(response.message);
            }
        }

        setLoading(false);
    };

    // ======================================================= //
    // ============ SUBSCRIPTION FLOW API CALLS ============== //

    // API - creating subscription
    const _createSubscription = async () => {

        let url = "subscription/create";
        let data = {
            "plan_id": plan_info.id,
            "planned_investment_amount": is_fixed_type ? Number(plan_info.fixed_fee) : selectedAmount,
            "user_id": USER_DATA.unique_identifier,
            "state": selectedState.label
        };

        const response = await APIService(url, data);
        if (response.status_code === 200) {
            if (is_agreement_required == false) {
                // if (response.data.plan.agreement_template == null) {
                _authorizeSubscription(response.data.id);
            } else {
                _signSubscription(response.data.id);
            }

        } else {
            toast.dismiss();
            toast.error(response.message);
            _updateSubscriptionStatus();
        };
    };

    // API - authorize subscription
    const _authorizeSubscription = async (subscription_id) => {

        let url = "subscription/authorize";
        let data = {
            payment_method: mandateType === 2 ? "UPI" : "NETBANKING",
            subscription_id: Number(subscription_id)
        };

        const response = await APIService(url, data);
        if (response.status_code === 200) {
            let authorized_subscription_id = Number(response.data.id);
            let rp_data = {
                order_id: response.data.transaction_id,
                razorpay_key: process.env.REACT_APP_RAZORPAY_KEY,
                customer_id: response.data.customer_id
            };
            // open razor payment modal
            _openRPGateway(rp_data, authorized_subscription_id);
            setMandateLoading(false);
        } else {
            toast.dismiss();
            toast.error(response.message);
            _updateSubscriptionStatus();
        }

    };

    // API - hitting razorpay SDK
    const _openRPGateway = (rp_data) => {

        let options = {
            "key": rp_data.razorpay_key,
            "order_id": rp_data.order_id,
            "customer_id": rp_data.customer_id,
            // "customer_id": "cust_PL6r181DTPnQY",
            "recurring": "1",
            // amount: Number(amountPayable.replace(/[^0-9.]/g, '')).toFixed(2),
            currency: 'INR',
            name: 'Indvestment',
            "image": require('../../Assets/Images/Navigation/indvestment-logo.svg').default,
            redirect: false,
            retry: { enabled: false, },
            "modal": {
                confirm_close: true,
                ondismiss: function (response) {

                    console.log(response);

                    if (response && response.error.code) {
                        toast.dismiss();
                        toast.error(response.error.description);
                    } else if (!response) {
                        // user closed the modal without making payment or any other issue
                        toast.dismiss();
                        toast.error("Please complete the payment to create mandate");
                        setMandateLoading(false);
                    }
                    _updateSubscriptionStatus();
                },
            },
            "theme": { color: Colors.ocean_blue },
            readonly: {
                contact: true,
                email: true,
                name: true,
            },
            "notes": {
                "note_key 1": "Beam me up Scotty",
                "note_key 2": "Tea. Earl Gray. Hot."
            },
            "prefill": {
                email: USER_DATA.email,
                contact: USER_DATA.mobile,
                name: `${USER_DATA.first_name} ${USER_DATA.middle_name}${USER_DATA.last_name}`
            },
            handler: () => {
                // Success handler
                setShowStatusLoader(true);
                setTimeout(() => {
                    // Showing subscription success modal
                    setShowSubscriptionStatusModal(true);
                    setShowStatusLoader(false);
                    setModalType(4);
                }, 5000);
            },
        };

        console.log(options);

        const razorpayInstance = new Razorpay(options);
        razorpayInstance.open();
        if (RazorPayError) {
            setMandateLoading(false);
            toast.dismiss();
            toast.error("Failed to open payment window. Try after some time")
        }
    };

    // API - sign subscription
    const _signSubscription = async (subscription_id) => {

        let url = "subscription/sign";
        let data = {
            "subscription_id": Number(subscription_id),
            "template": {
                "additional_prop1": "String1",
                "additional_prop2": "String2",
                "additional_prop3": "String3"
            }
        };

        const response = await APIService(url, data);
        if (response.status_code === 200) {
            let document_id = response.data.agreement_id;
            let identifier = USER_DATA.mobile;
            let options = {
                environment: "sandbox",
                logo: require('../../Assets/Images/Navigation/indvestment-logo.svg').default,
                theme: {
                    primaryColor: Colors.ocean_blue,
                    secondaryColor: Colors.black
                },
                callback: function (digio_response) {

                    if (digio_response.hasOwnProperty("error_code")) {
                        toast.dismiss();
                        toast.error(digio_response.message);
                        setMandateLoading(false);
                        _updateSubscriptionStatus();
                        return;
                    } else {
                        _syncSubscription(response.data.id);
                    }
                    setMandateLoading(false);

                },
            };

            let digio = new window.Digio(options);
            digio.init();
            digio.submit(document_id, identifier);

        } else {
            toast.dismiss();
            toast.error("Something went wrong, please try again");
            _updateSubscriptionStatus();
        }
    };

    // API - sync subscription
    const _syncSubscription = async (agreement_id) => {
        let url = 'subscription/sync';
        let data = {
            "id": Number(agreement_id)
        };

        const response = await APIService(url, data);
        if (response.status_code === 200) {
            setShowSubscriptionStatusModal(true);
            setModalType(3);
        } else {
            toast.dismiss();
            toast.error(response.message);
        }
        _updateSubscriptionStatus();
    };

    // API - update status
    const _updateSubscriptionStatus = async () => {

        let url = "products/strategy-by-id";
        let data = {
            "strategy_id": Number(productData.strategy_id),
            "user_id": USER_DATA.unique_identifier
        };

        let response = await APIService(url, data)
        if (response.status_code === 200) {
            setProductData(response.data);
        } else {
            toast.dismiss();
            toast.error("No data found");
            navigate("/explore");
        }

        setMandateLoading(false);
    };

    // =============================================== //
    // ============ Inline component ================= //

    const StatusLoader = () => {
        return (
            <div className={`w-100 h-100 position-fixed top-0 start-0 d-flex justify-content-center align-items-center z-3`}>
                {/* overlay */}
                <div className="overlay-background opacity-75 cursor-default"></div>
                <div className="div bg-white  z-index-99 border-radius-8">
                    <img src={LoaderImg}
                        draggable="false"
                        className="h-48"
                        alt='loader' />
                </div>
            </div>
        )
    };

    return (
        <section className='min-vh-100 mb-5 card-box-shadow h-100 e-bg-white p-0'>

            <div className={`${style.e_top_section} border-radius-16 p-3 p-sm-4`}>
                {/* Product short info */}
                <div className='d-flex align-items-center gap-12px pb-3'>
                    <img src={productData.strategy_icon}
                        draggable={false}
                        className='border-radius-4 bg-white p-1 object-fit-contain'
                        width={56}
                        height={56}
                        alt={productData.strategy_name} />
                    <div>
                        <p className='e-font-14-res mb-1 e-text-white e-alt-font-lato e-font-18 e-font-weight-600 line-height-20px'>
                            {productData.strategy_name}
                        </p>
                        {
                            productData.creator_info &&
                            <span className='e-font-12-res mb-0 e-text-magnolia e-alt-font-lato e-font-16 e-font-weight-400 line-height-18px text-decoration-hover-underline cursor-pointer'
                                onClick={(event) => _handleNavigateToCreatorDetail(event, productData.creator_info.creator_id)}>
                                by {productData.creator_info.creator_name || "--"}
                            </span>
                        }
                    </div>
                </div>
                {/* Step flow */}
                <div className='d-flex flex-column flex-md-row'>
                    <div className='col flex-1 d-flex flex-md-column flex-row'>
                        <div className='d-flex align-items-center flex-column flex-md-row'>
                            <img src={step === 1 ? ActiveStep1 : CompletedStep}
                                draggable={false}
                                className={`h-24`}
                                alt='icon' />
                            <div className='e-bg-pale-lavender h-2 h-40px-res-md width-100 w-2px-res-md'> </div>
                        </div>
                        <div className='padding-12px-top e-padding-top-0-res-md ps-3 ps-md-0'>
                            <p className='e-font-14-res mb-0 e-primary-font-clash-display e-text-white e-font-16 e-font-weight-500 line-height-24px'>
                                Verify KYC
                            </p>
                            {/* <p className='mb-0 e-alt-font-lato e-text-magnolia e-font-12 e-font-weight-400 line-height-20px pe-1'>
                                Lorem ipsum dolor sit amet.
                            </p> */}
                        </div>
                    </div>

                    {/* Risk profile or Subscription Mandate */}
                    <div className='col flex-1 d-flex flex-md-column flex-row'>
                        <div className='d-flex align-items-center flex-column flex-md-row'>
                            <img src={step > 2 ? CompletedStep : step === 2 ? ActiveStep2 : InActiveStep2}
                                draggable={false}
                                className={`h-24`}
                                alt='icon' />
                            {
                                is_agreement_required === true &&
                                <div className='e-bg-pale-lavender h-2 h-40px-res-md width-100 w-2px-res-md'> </div>
                            }
                        </div>
                        <div className='padding-12px-top e-padding-top-0-res-md ps-3 ps-md-0'>
                            <p className='e-font-14-res mb-0 e-primary-font-clash-display e-text-white e-font-16 e-font-weight-500 line-height-24px'>
                                {
                                    is_agreement_required === false ? "Subscription" : "Risk Profile"
                                }
                            </p>
                            {/* <p className='mb-0 e-alt-font-lato e-text-magnolia e-font-12 e-font-weight-400 line-height-20px pe-1'>
                                {
                                    is_agreement_required === false ? "Lorem ipsum dolor sit amet consectetur." : "Lorem ipsum dolor sit amet consectetur."
                                }
                            </p> */}
                        </div>
                    </div>

                    {
                        is_agreement_required === true &&
                        <>
                            <div className='col flex-1 d-flex flex-md-column flex-row'>
                                <div className='d-flex align-items-center flex-column flex-md-row'>
                                    <img src={step > 3 ? CompletedStep : step === 3 ? ActiveStep3 : InActiveStep3}
                                        draggable={false}
                                        className={`h-24`}
                                        alt='icon' />
                                    {
                                        is_agreement_required &&
                                        <div className='e-bg-pale-lavender h-2 h-40px-res-md width-100 w-2px-res-md'> </div>
                                    }
                                </div>
                                <div className='padding-12px-top e-padding-top-0-res-md ps-3 ps-md-0'>
                                    <p className='e-font-14-res mb-0 e-primary-font-clash-display e-text-white e-font-16 e-font-weight-500 line-height-24px'>
                                        E-sign Investor Agreement
                                    </p>
                                    {/* <p className='mb-0 e-alt-font-lato e-text-magnolia e-font-12 e-font-weight-400 line-height-20px pe-1'>
                                        Lorem ipsum dolor sit amet consectetur.
                                    </p> */}
                                </div>
                            </div>
                            <div className='col flex-1 d-flex flex-md-column flex-row'>
                                <div className='d-flex align-items-center flex-column flex-md-row'>
                                    <img src={step > 4 ? CompletedStep : step === 4 ? ActiveStep4 : InActiveStep4}
                                        draggable={false}
                                        className={`h-24`}
                                        alt='icon' />
                                </div>
                                <div className='padding-12px-top e-padding-top-0-res-md ps-3 ps-md-0'>
                                    <p className='e-font-14-res mb-0 e-primary-font-clash-display e-text-white e-font-16 e-font-weight-500 line-height-24px'>
                                        Subscription
                                    </p>
                                    {/* <p className='mb-0 e-alt-font-lato e-text-magnolia e-font-12 e-font-weight-400 line-height-20px pe-1'>
                                        Lorem ipsum dolor sit amet.
                                    </p> */}
                                </div>
                            </div>
                        </>
                    }
                </div>
            </div>

            {
                dataLoading ?
                    <Loader wrapperClassName="h-100 margin-100px-top" />
                    :
                    <div className='padding-30px-tb px-3'>
                        <div className='row'>
                            <div className='col-md-10 col-lg-7'>
                                <TextButton name={
                                    step === 1 ? "Complete your KYC Verification"
                                        : step === 2 && is_agreement_required ? "Risk Profile"
                                            : step === 2 && is_agreement_required === false ? "Subscription Preview"
                                                : step === 3 && is_agreement_required ? "E-sign Investor Agreement" : "Subscription Preview"}
                                    icon={true}
                                    type={2}
                                    onPress={_handleBack} />
                                {
                                    (step !== 2 && step !== 4) &&
                                    <p className='pt-3 mb-4 e-alt-font-lato e-text-outer-space e-font-14 e-font-weight-400 line-height-20px'>
                                        {
                                            step === 1 ? "Please provide your PAN and Date of Birth to complete the KYC verification process and start investing seamlessly."
                                                : step === 3 && is_agreement_required ? "Use your Aadhaar to complete the e-sign process for the investor agreement, ensuring a quick and secure verification." : ""
                                        }
                                    </p>
                                }

                                <div ref={scrollRef}
                                    className='row min-vh-100'>
                                    <div className='col-sm-10'>
                                        {
                                            step === 1 ?
                                                // KYC Validation
                                                <div>
                                                    {/* PAN */}
                                                    <CustomTextInput label="Permanent Account Number"
                                                        placeholder="Enter PAN"
                                                        type="text"
                                                        require
                                                        value={pan}
                                                        error={panError}
                                                        className="w-100 bg-transparent"
                                                        // onKeyPress={_handleVerifyKYC}
                                                        handleChange={_handlePanChange} />

                                                    {/* Date of Birth* */}
                                                    <CustomDatePicker label="Date of Birth"
                                                        placeholder="Choose DOB"
                                                        require
                                                        overlay={true}
                                                        selected={selectedDate}
                                                        toggle={dateToggle}
                                                        error={dateError}
                                                        wrapperClass="pt-4"
                                                        startDate={subYears(new Date(), 18)}
                                                        minDate={subYears(new Date(), 100)}
                                                        maxDate={subYears(new Date(), 18)}
                                                        handleToggle={_handleDateToggle}
                                                        handleClose={_handleDateToggle}
                                                        handleClear={_handleClearDate}
                                                        handleSelect={_handleSelectDate} />
                                                    {/* category select */}
                                                    <div>
                                                        <label className={` e-text-chinese-black e-font-14 e-alt-font-lato  e-font-weight-500 line-height-20px`}>
                                                            Select your state
                                                            <span className='e-text-vermilion'>*</span>
                                                        </label>
                                                        <CustomSelectBox value={selectedState}
                                                            error={stateError}
                                                            type={2}
                                                            options={states}
                                                            borderRadius="8px"
                                                            background="#FFFFFF"
                                                            padding="8px 8px"
                                                            width="100%"
                                                            fontSize="14px"
                                                            menuWidth="100%"
                                                            menuBorderRadius="8px"
                                                            wrapperClass="padding-12px-tb"
                                                            selected={false}
                                                            menuPortalTarget={document.body}
                                                            isDisabled={stateDataLoading}
                                                            selectChange={_handleSelectState} />
                                                    </div>

                                                    <PrimaryButton name="Continue"
                                                        status={loading}
                                                        disabled={loading}
                                                        className="w-100 mt-4 h-48"
                                                        onPress={_handleVerifyKYC} />
                                                </div>
                                                :
                                                (step === 2 && is_agreement_required === false) ? (
                                                    // Subscription - Mandate
                                                    <div className='pt-4'>
                                                        <div className={`${CardStyle.e_alphabasket_card_wrapper}`}>
                                                            <div className='e-bg-white border-radius-16'>
                                                                <div data-type={(productData.strategy_type === 'alpha-basket' || productData.strategy_type === null) ? "basket" : "basket-pro"}
                                                                    className={`${CardStyle.e_heading_wrapper} position-relative`}>
                                                                    <img src={require((productData.strategy_type === 'alpha-basket' || productData.strategy_type === null) ? "../../Assets/Images/Card/alphabasket-tag.svg" : "../../Assets/Images/Card/alphabasket-pro-tag.svg").default}
                                                                        draggable={false}
                                                                        className='position-absolute left--6px top-8px h-28'
                                                                        alt='tag' />
                                                                    <Chip label={productData.risk_category_info?.risk_category_name}
                                                                        className={productData.risk_category_info?.risk_category_name === 'Conservative' ? 'e-text-deep-green e-bg-nyanza e-border-1px-light-green' :
                                                                            productData.risk_category_info?.risk_category_name === 'Moderate' ? 'e-text-brown e-bg-lemon-chiffon e-border-1px-jasmine' :
                                                                                'e-text-cadmium-purple e-bg-lavender-blush e-border-1px-light-red'} />
                                                                </div>
                                                                <div className='p-3'>

                                                                    {/* Strategy details */}
                                                                    <div className='d-flex align-items-center gap-12px e-border-bottom-1px-anti-flash-white pb-3'>
                                                                        <img src={productData.strategy_icon}
                                                                            draggable={false}
                                                                            className='border-radius-4 bg-white'
                                                                            width={48}
                                                                            alt={productData.strategy_name} />
                                                                        <div>
                                                                            <p className='e-font-14-res mb-1 e-text-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px'>
                                                                                {productData.strategy_name}
                                                                            </p>
                                                                            <span className='e-font-12-res mb-0 e-text-philippine-gray e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px text-decoration-hover-underline cursor-pointer'
                                                                                onClick={() => _handleNavigateToCreatorDetail(productData.creator_info.creator_id)}>
                                                                                by {productData.creator_info.creator_name || "--"}
                                                                            </span>
                                                                        </div>
                                                                    </div>

                                                                    <div className='row justify-content-between pt-2'>
                                                                        {
                                                                            productData.returns.value ?
                                                                                <div className='col-3'>
                                                                                    <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-12 e-font-weight-400 line-height-18px'>
                                                                                        {productData.returns.label} CAGR
                                                                                    </p>
                                                                                    <p className={`${productData.returns.value >= 0 ? "e-text-islamic-green-light" : "e-text-red"} e-font-14-res mb-0 e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px`}>
                                                                                        {productData.returns.value}%
                                                                                    </p>
                                                                                </div>
                                                                                :
                                                                                ""
                                                                        }
                                                                        <div className='col-5'>
                                                                            <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-12 e-font-weight-400 line-height-18px'>
                                                                                Objective
                                                                            </p>
                                                                            <p className={`e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px`}>
                                                                                {productData.objective}
                                                                            </p>
                                                                        </div>
                                                                        <div className='col-4 text-end'>
                                                                            <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-12 e-font-weight-400 line-height-18px'>
                                                                                Min. Investment
                                                                            </p>
                                                                            {
                                                                                productData.min_investment &&
                                                                                <p className={`e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px`}>
                                                                                    ₹{Number(productData.min_investment).toLocaleString("en-IN")}
                                                                                </p>
                                                                            }
                                                                        </div>
                                                                    </div>

                                                                    {/* Selecting mandate payment type */}
                                                                    <div className='my-3'>
                                                                        <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                            Select Mandate Type
                                                                        </span>
                                                                        <div className='d-flex gap-3 py-2'>
                                                                            {MANDATE_TYPE.map((data, key) => (
                                                                                <div key={key}>
                                                                                    <CustomRadioButton data={data}
                                                                                        selected={mandateType}
                                                                                        handleSelect={_handleSelectMandateType} />
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>


                                                                    {/* Subscription payment info */}
                                                                    <div className="d-flex flex-column gap-6px e-bg-cultured-light border-radius-8 padding-12px-tb padding-22px-lr mb-3">

                                                                        <div className='d-flex justify-content-between'>
                                                                            <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                {is_fixed_type ? "Subscription fee" : "Subscription percentage"}
                                                                            </span>
                                                                            <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                <span>
                                                                                    {plan_info.fee_text}
                                                                                    {
                                                                                        plan_info.fee_text === "FIXED" &&
                                                                                        <span className='e-font-12'>
                                                                                            /{plan_info.duration_text}
                                                                                        </span>
                                                                                    }
                                                                                </span>
                                                                            </span>
                                                                        </div>

                                                                        {
                                                                            is_fixed_type == false && (
                                                                                <>
                                                                                    <div className='d-flex justify-content-between'>
                                                                                        <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                            Subscription duration
                                                                                        </span>
                                                                                        <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                            {plan_info.duration_text}
                                                                                        </span>
                                                                                    </div>

                                                                                    <div className='py-1'>
                                                                                        <p className='d-flex justify-content-between e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px mb-0'>
                                                                                            Investment Amount
                                                                                            <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                                ₹{selectedAmount.toLocaleString("en-IN")}
                                                                                            </span>
                                                                                        </p>
                                                                                        {/* Amount picker */}
                                                                                        <div className='padding-42px-top pe-2'>
                                                                                            <CustomSlider
                                                                                                type={2}
                                                                                                hasTooltip={true}
                                                                                                toolTipValue={`₹${selectedAmount.toLocaleString("en-IN")}`}
                                                                                                min={productData.min_investment}
                                                                                                max={1000000}
                                                                                                minLabel={`₹${productData.min_investment.toLocaleString("en-IN")}`}
                                                                                                maxLabel={`₹10,00,000`}
                                                                                                className={"slider-type-2 d-flex align-items-center"}
                                                                                                wrapperClassName=""
                                                                                                disabled={agreement_status === "REQUESTED" ? true : false}
                                                                                                step={500}
                                                                                                defaultValue={selectedAmount}
                                                                                                handleChange={_handleAmountValueChange} />
                                                                                        </div>
                                                                                    </div>
                                                                                </>
                                                                            )
                                                                        }
                                                                        {
                                                                            (selectedState.label !== productData.creator_info.state) ?
                                                                                <div className='d-flex justify-content-between'>
                                                                                    <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                        {selectedState ? "IGST" : "GST"}({plan_info.igst}%)
                                                                                    </span>
                                                                                    <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                        ₹{IGST}
                                                                                    </span>
                                                                                </div>
                                                                                :
                                                                                <>
                                                                                    <div className='d-flex justify-content-between'>
                                                                                        <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                            SGST({plan_info.sgst}%)
                                                                                        </span>
                                                                                        <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                            ₹{SGST}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className='d-flex justify-content-between '>
                                                                                        <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                            CGST({plan_info.sgst}%)
                                                                                        </span>
                                                                                        <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                            ₹{CGST}
                                                                                        </span>
                                                                                    </div>
                                                                                </>
                                                                        }

                                                                        {/* Total amount payable */}
                                                                        <div className='d-flex justify-content-between'>
                                                                            <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                Total Amount Payable
                                                                            </span>
                                                                            <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                ₹{amountPayable}
                                                                            </span>
                                                                        </div>
                                                                    </div>

                                                                    {/* Mandate creation button */}
                                                                    <PrimaryButton name={btnLabel}
                                                                        status={mandateLoading}
                                                                        disabled={mandateLoading}
                                                                        className="w-100 h-48 e-font-16 mt-2"
                                                                        onPress={_handleSubscribe} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                                    : (step === 2 && is_agreement_required) ?
                                                        // Risk Assessment
                                                        <RiskProfiling basketName={productData.strategy_name}
                                                            strategyID={productData.strategy_id}
                                                            tenantID={tenant_id}
                                                            userID={USER_DATA.unique_identifier}
                                                            basketRiskInfo={productData.risk_category_info}
                                                            // riskHistory={riskHistory}
                                                            handleOnSuccess={_handleRiskProfileSuccess} />
                                                        :
                                                        (step === 3 && is_agreement_required) ? (
                                                            // Subscription  - Agreement Sign
                                                            <div className='pt-4'>
                                                                <div className={`${CardStyle.e_alphabasket_card_wrapper}`}>
                                                                    <div className='e-bg-white border-radius-16'>
                                                                        <div data-type={(productData.strategy_type === 'alpha-basket' || productData.strategy_type === null) ? "basket" : "basket-pro"}
                                                                            className={`${CardStyle.e_heading_wrapper} position-relative`}>
                                                                            <img src={require((productData.strategy_type === 'alpha-basket' || productData.strategy_type === null) ? "../../Assets/Images/Card/alphabasket-tag.svg" : "../../Assets/Images/Card/alphabasket-pro-tag.svg").default}
                                                                                draggable={false}
                                                                                className='position-absolute left--6px top-8px h-28'
                                                                                alt='tag' />
                                                                            <Chip label={productData.risk_category_info?.risk_category_name}
                                                                                className={productData.risk_category_info?.risk_category_name === 'Conservative' ? 'e-text-deep-green e-bg-nyanza e-border-1px-light-green' :
                                                                                    productData.risk_category_info?.risk_category_name === 'Moderate' ? 'e-text-brown e-bg-lemon-chiffon e-border-1px-jasmine' :
                                                                                        'e-text-cadmium-purple e-bg-lavender-blush e-border-1px-light-red'} />
                                                                        </div>
                                                                        <div className='p-3'>

                                                                            {/* Strategy details */}
                                                                            <div className='d-flex align-items-center gap-12px e-border-bottom-1px-anti-flash-white pb-3'>
                                                                                <img src={productData.strategy_icon}
                                                                                    draggable={false}
                                                                                    className='border-radius-4 bg-white'
                                                                                    width={48}
                                                                                    alt={productData.strategy_name} />
                                                                                <div>
                                                                                    <p className='e-font-14-res mb-1 e-text-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px'>
                                                                                        {productData.strategy_name}
                                                                                    </p>
                                                                                    <span className='e-font-12-res mb-0 e-text-philippine-gray e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px text-decoration-hover-underline cursor-pointer'
                                                                                        onClick={() => _handleNavigateToCreatorDetail(productData.creator_info.creator_id)}>
                                                                                        by {productData.creator_info.creator_name || "--"}
                                                                                    </span>
                                                                                </div>
                                                                            </div>

                                                                            <div className='row justify-content-between pt-2'>
                                                                                {
                                                                                    productData.returns.value ?
                                                                                        <div className='col-3'>
                                                                                            <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-12 e-font-weight-400 line-height-18px'>
                                                                                                {productData.returns.label} CAGR
                                                                                            </p>
                                                                                            <p className={`${productData.returns.value >= 0 ? "e-text-islamic-green-light" : "e-text-red"} e-font-14-res mb-0 e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px`}>
                                                                                                {productData.returns.value}%
                                                                                            </p>
                                                                                        </div>
                                                                                        :
                                                                                        ""
                                                                                }
                                                                                <div className='col-5'>
                                                                                    <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-12 e-font-weight-400 line-height-18px'>
                                                                                        Objective
                                                                                    </p>
                                                                                    <p className={`e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px`}>
                                                                                        {productData.objective}
                                                                                    </p>
                                                                                </div>
                                                                                <div className='col-4 text-end'>
                                                                                    <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-12 e-font-weight-400 line-height-18px'>
                                                                                        Min. Investment
                                                                                    </p>
                                                                                    {
                                                                                        productData.min_investment &&
                                                                                        <p className={`e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px`}>
                                                                                            ₹{Number(productData.min_investment).toLocaleString("en-IN")}
                                                                                        </p>
                                                                                    }
                                                                                </div>
                                                                            </div>

                                                                            {/* Subscription payment info */}
                                                                            <div className="d-flex flex-column gap-6px e-bg-cultured-light border-radius-8 padding-12px-tb padding-22px-lr my-3">

                                                                                <div className='d-flex justify-content-between'>
                                                                                    <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                        {is_fixed_type ? "Subscription fee" : "Subscription percentage"}
                                                                                    </span>
                                                                                    <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                        <span>
                                                                                            {plan_info.fee_text}
                                                                                            {
                                                                                                plan_info.fee_text === "FIXED" &&
                                                                                                <span className='e-font-12'>
                                                                                                    /{plan_info.duration_text}
                                                                                                </span>
                                                                                            }
                                                                                        </span>
                                                                                    </span>
                                                                                </div>



                                                                                {
                                                                                    is_fixed_type == false && (
                                                                                        <>
                                                                                            <div className='d-flex justify-content-between'>
                                                                                                <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                                    Subscription duration
                                                                                                </span>
                                                                                                <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                                    {plan_info.duration_text}
                                                                                                </span>
                                                                                            </div>

                                                                                            <div>
                                                                                                <p className='d-flex justify-content-between e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px mb-0'>
                                                                                                    Investment Amount
                                                                                                    <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                                        ₹{selectedAmount.toLocaleString("en-IN")}
                                                                                                    </span>
                                                                                                </p>
                                                                                                {/* Amount picker */}
                                                                                                <div className='padding-42px-top pe-2'>
                                                                                                    <CustomSlider
                                                                                                        type={2}
                                                                                                        hasTooltip={true}
                                                                                                        toolTipValue={`₹${selectedAmount.toLocaleString("en-IN")}`}
                                                                                                        min={productData.min_investment}
                                                                                                        max={1000000}
                                                                                                        minLabel={`₹${productData.min_investment.toLocaleString("en-IN")}`}
                                                                                                        maxLabel={`₹10,00,000`}
                                                                                                        className={"slider-type-2 d-flex align-items-center"}
                                                                                                        wrapperClassName=""
                                                                                                        disabled={agreement_status === "REQUESTED" ? true : false}
                                                                                                        step={500}
                                                                                                        defaultValue={selectedAmount}
                                                                                                        handleChange={_handleAmountValueChange} />
                                                                                                </div>

                                                                                                {/* Advisory fees */}
                                                                                                <div className='d-flex justify-content-between mt-3'>
                                                                                                    <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                                        Advisory fees
                                                                                                    </span>
                                                                                                    <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                                        ₹{advisoryFees}
                                                                                                    </span>
                                                                                                </div>
                                                                                            </div>
                                                                                        </>
                                                                                    )
                                                                                }



                                                                                {
                                                                                    (selectedState.label !== productData.creator_info.state) ?
                                                                                        <div className='d-flex justify-content-between'>
                                                                                            <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                                {selectedState ? "IGST" : "GST"}({plan_info.igst}%)
                                                                                            </span>
                                                                                            <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                                ₹{IGST}
                                                                                            </span>
                                                                                        </div>
                                                                                        :
                                                                                        <>
                                                                                            <div className='d-flex justify-content-between'>
                                                                                                <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                                    SGST({plan_info.sgst}%)
                                                                                                </span>
                                                                                                <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                                    ₹{SGST}
                                                                                                </span>
                                                                                            </div>
                                                                                            <div className='d-flex justify-content-between '>
                                                                                                <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                                    CGST({plan_info.sgst}%)
                                                                                                </span>
                                                                                                <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                                    ₹{CGST}
                                                                                                </span>
                                                                                            </div>
                                                                                        </>
                                                                                }

                                                                                {/* Total amount payable */}
                                                                                <div className='d-flex justify-content-between'>
                                                                                    <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                        Total Amount Payable
                                                                                    </span>
                                                                                    <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                        ₹{amountPayable}
                                                                                    </span>
                                                                                </div>
                                                                            </div>



                                                                            {/* Mandate creation button */}
                                                                            <PrimaryButton name={btnLabel}
                                                                                status={mandateLoading}
                                                                                disabled={mandateLoading}
                                                                                className="w-100 h-48 e-font-16 mt-2"
                                                                                onPress={_handleSubscribe} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                            :
                                                            step === 4 ? (
                                                                // Subscription Mandate - AutoPay
                                                                <div className='pt-4'>
                                                                    <div className={`${CardStyle.e_alphabasket_card_wrapper}`}>
                                                                        <div className='e-bg-white border-radius-16'>
                                                                            <div data-type={(productData.strategy_type === 'alpha-basket' || productData.strategy_type === null) ? "basket" : "basket-pro"}
                                                                                className={`${CardStyle.e_heading_wrapper} position-relative`}>
                                                                                <img src={require((productData.strategy_type === 'alpha-basket' || productData.strategy_type === null) ? "../../Assets/Images/Card/alphabasket-tag.svg" : "../../Assets/Images/Card/alphabasket-pro-tag.svg").default}
                                                                                    draggable={false}
                                                                                    className='position-absolute left--6px top-8px h-28'
                                                                                    alt='tag' />
                                                                                <Chip label={productData.risk_category_info?.risk_category_name}
                                                                                    className={productData.risk_category_info?.risk_category_name === 'Conservative' ? 'e-text-deep-green e-bg-nyanza e-border-1px-light-green' :
                                                                                        productData.risk_category_info?.risk_category_name === 'Moderate' ? 'e-text-brown e-bg-lemon-chiffon e-border-1px-jasmine' :
                                                                                            'e-text-cadmium-purple e-bg-lavender-blush e-border-1px-light-red'} />
                                                                            </div>
                                                                            <div className='p-3'>

                                                                                {/* Strategy details */}
                                                                                <div className='d-flex align-items-center gap-12px e-border-bottom-1px-anti-flash-white pb-3'>
                                                                                    <img src={productData.strategy_icon}
                                                                                        draggable={false}
                                                                                        className='border-radius-4 bg-white'
                                                                                        width={48}
                                                                                        alt={productData.strategy_name} />
                                                                                    <div>
                                                                                        <p className='e-font-14-res mb-1 e-text-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px'>
                                                                                            {productData.strategy_name}
                                                                                        </p>
                                                                                        <span className='e-font-12-res mb-0 e-text-philippine-gray e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px text-decoration-hover-underline cursor-pointer'
                                                                                            onClick={() => _handleNavigateToCreatorDetail(productData.creator_info.creator_id)}>
                                                                                            by {productData.creator_info.creator_name || "--"}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>

                                                                                <div className='row justify-content-between pt-2'>
                                                                                    {
                                                                                        productData.returns.value ?
                                                                                            <div className='col-3'>
                                                                                                <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-12 e-font-weight-400 line-height-18px'>
                                                                                                    {productData.returns.label} CAGR
                                                                                                </p>
                                                                                                <p className={`${productData.returns.value >= 0 ? "e-text-islamic-green-light" : "e-text-red"} e-font-14-res mb-0 e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px`}>
                                                                                                    {productData.returns.value}%
                                                                                                </p>
                                                                                            </div>
                                                                                            :
                                                                                            ""
                                                                                    }
                                                                                    <div className='col-5'>
                                                                                        <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-12 e-font-weight-400 line-height-18px'>
                                                                                            Objective
                                                                                        </p>
                                                                                        <p className={`e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px`}>
                                                                                            {productData.objective}
                                                                                        </p>
                                                                                    </div>
                                                                                    <div className='col-4 text-end'>
                                                                                        <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-12 e-font-weight-400 line-height-18px'>
                                                                                            Min. Investment
                                                                                        </p>
                                                                                        {
                                                                                            productData.min_investment &&
                                                                                            <p className={`e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px`}>
                                                                                                ₹{Number(productData.min_investment).toLocaleString("en-IN")}
                                                                                            </p>
                                                                                        }
                                                                                    </div>
                                                                                </div>

                                                                                {/* Selecting mandate payment type */}
                                                                                <div className='my-3'>
                                                                                    <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                        Select Mandate Type
                                                                                    </span>
                                                                                    <div className='d-flex gap-3 py-2'>
                                                                                        {MANDATE_TYPE.map((data, key) => (
                                                                                            <div key={key}>
                                                                                                <CustomRadioButton data={data}
                                                                                                    selected={mandateType}
                                                                                                    handleSelect={_handleSelectMandateType} />
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>


                                                                                {/* Subscription payment info */}
                                                                                <div className="d-flex flex-column gap-6px e-bg-cultured-light border-radius-8 padding-12px-tb padding-22px-lr mb-3">

                                                                                    <div className='d-flex justify-content-between'>
                                                                                        <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                            {is_fixed_type ? "Subscription fee" : "Subscription percentage"}
                                                                                        </span>
                                                                                        <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                            <span>
                                                                                                {plan_info.fee_text}
                                                                                                {
                                                                                                    plan_info.fee_text === "FIXED" &&
                                                                                                    <span className='e-font-12'>
                                                                                                        /{plan_info.duration_text}
                                                                                                    </span>
                                                                                                }
                                                                                            </span>
                                                                                        </span>
                                                                                    </div>


                                                                                    {
                                                                                        is_fixed_type == false && (
                                                                                            <>
                                                                                                <div className='d-flex justify-content-between'>
                                                                                                    <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                                        Subscription duration
                                                                                                    </span>
                                                                                                    <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                                        {plan_info.duration_text}
                                                                                                    </span>
                                                                                                </div>

                                                                                                <div>
                                                                                                    <p className='d-flex justify-content-between e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px mb-0'>
                                                                                                        Investment Amount
                                                                                                        <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                                            ₹{selectedAmount.toLocaleString("en-IN")}
                                                                                                        </span>
                                                                                                    </p>

                                                                                                    {/* Advisory fees */}
                                                                                                    <div className='d-flex justify-content-between mt-3'>
                                                                                                        <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                                            Advisory fees
                                                                                                        </span>
                                                                                                        <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                                            ₹{advisoryFees}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </>
                                                                                        )
                                                                                    }

                                                                                    {
                                                                                        selectedState.label !== productData.creator_info.state ?
                                                                                            <div className='d-flex justify-content-between'>
                                                                                                <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                                    {selectedState ? "IGST" : "GST"}({plan_info.igst}%)
                                                                                                </span>
                                                                                                <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                                    ₹{IGST}
                                                                                                </span>
                                                                                            </div>
                                                                                            :
                                                                                            <>
                                                                                                <div className='d-flex justify-content-between'>
                                                                                                    <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                                        SGST({plan_info.sgst}%)
                                                                                                    </span>
                                                                                                    <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                                        ₹{SGST}
                                                                                                    </span>
                                                                                                </div>
                                                                                                <div className='d-flex justify-content-between '>
                                                                                                    <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                                        CGST({plan_info.sgst}%)
                                                                                                    </span>
                                                                                                    <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                                        ₹{CGST}
                                                                                                    </span>
                                                                                                </div>
                                                                                            </>
                                                                                    }

                                                                                    {/* Total amount payable */}
                                                                                    <div className='d-flex justify-content-between'>
                                                                                        <span className='e-text-outer-space e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                                                                            Total Amount Payable
                                                                                        </span>
                                                                                        <span className='e-text-chinese-black  e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px e-letter-spacing-min-point-1px'>
                                                                                            ₹{amountPayable}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>



                                                                                {/* Mandate creation button */}
                                                                                <PrimaryButton name={btnLabel}
                                                                                    status={mandateLoading}
                                                                                    disabled={mandateLoading}
                                                                                    className="w-100 h-48 e-font-16 mt-2"
                                                                                    onPress={_handleSubscribe} />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )
                                                                :
                                                                null

                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
            }


            {/* KYC status and Subscription Status */}
            {
                showSubscriptionStatusModal &&
                <SubscriptionStatusModal
                    type={modalType}
                    handleClose={_handleCloseStatusModal}
                    handleSuccess={_handleNavigateToDetailPage} />
            }

            {
                showStatusLoader &&
                <StatusLoader />
            }
        </section>
    )
}

export default CreateSubscription;

